import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import type { WorkOrder, PaginatedResponse } from '@/types'

// ALLINEATO AL BACKEND SCHEMA
export interface WorkOrderCreate {
  vehicle_id?: number | null
  customer_id?: number | null
  numero_scheda?: string
  data_appuntamento?: string | null
  data_fine_prevista?: string | null
  data_completamento?: string | null
  tipo_danno?: string
  priorita?: string
  valutazione_danno?: string | null
  note?: string
  stato?: string
  creato_da?: number
  approvato_da?: number
  auto_cortesia_id?: number | null
  costo_stimato?: number | null
  costo_finale?: number | null
  sinistro?: boolean
  ramo_sinistro_id?: number | null
  numero_sinistro?: string | null
  compagnia_sinistro?: string | null
  compagnia_debitrice_sinistro?: string | null
  scoperto?: number | null
  franchigia?: number | null
  legale?: string | null
  autorita?: string | null
  data_fine_prevista_consegna?: string | null
  data_entrata_veicolo?: string | null
  data_inizio_analisi?: string | null
  tecnico_analisi_id?: number | null
}

export const useWorkOrders = (
  page: number = 1,
  size: number = 10,
  search?: string,
  status?: string,
  sinistro?: boolean,
  scadute?: boolean,
  orderBy?: string,
  orderDir?: 'asc' | 'desc',
  rdaStatoNome?: string,
  customerId?: number,
  hasRda?: boolean,
  soloRiconsegnate?: boolean,
  entrataA?: string,
) => {
  return useQuery({
    queryKey: ['work-orders', page, size, search, status, sinistro, scadute, orderBy, orderDir, rdaStatoNome, customerId, hasRda, soloRiconsegnate, entrataA],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({
          skip: String((page - 1) * size),
          limit: String(size),
        })
        if (search) params.append('search', search)
        if (status) params.append('stato', status)
        if (sinistro) params.append('sinistro', 'true')
        if (scadute) params.append('scadute', 'true')
        if (orderBy) params.append('order_by', orderBy)
        if (orderDir) params.append('order_dir', orderDir)
        if (rdaStatoNome) params.append('rda_stato_nome', rdaStatoNome)
        if (customerId) params.append('customer_id', String(customerId))
        if (hasRda) params.append('has_rda', 'true')
        if (soloRiconsegnate) params.append('solo_riconsegnate', 'true')
        if (entrataA) params.append('entrata_a', entrataA)
        const url = `${API_ENDPOINTS.WORK_ORDERS}/?${params.toString()}`
        const response = await axiosInstance.get<PaginatedResponse<WorkOrder>>(url)
        return response.data
      } catch (error: any) {
        console.error('❌ Error loading work orders:', error)
        if (error.response) {
          console.error('Response status:', error.response.status)
          console.error('Response data:', error.response.data)
        }
        throw error
      }
    },
    retry: 1,
    staleTime: 0,
    refetchInterval: 60_000,
  })
}

export const useWorkOrder = (id: number) => {
  return useQuery({
    queryKey: ['work-order', id],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get<WorkOrder>(
          `${API_ENDPOINTS.WORK_ORDERS}/${id}`
        )
        return response.data
      } catch (error: any) {
        console.error('❌ Error loading work order:', error.message)
        throw error
      }
    },
    enabled: !!id,
    retry: 1,
    staleTime: 0,  // Always fetch fresh data
  })
}

export const useCreateWorkOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: WorkOrderCreate) => {
      const response = await axiosInstance.post<WorkOrder>(
        `${API_ENDPOINTS.WORK_ORDERS}/`,
        data
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      message.success('Accettazione creata con successo')
    },
    onError: () => {
      message.error('Errore durante la creazione dell\'accettazione')
    },
  })
}

export const useUpdateWorkOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<WorkOrderCreate> }) => {
      const response = await axiosInstance.put<WorkOrder>(
        `${API_ENDPOINTS.WORK_ORDERS}/${id}`,
        data
      )
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      queryClient.invalidateQueries({ queryKey: ['work-order', variables.id] })
      message.success('Ordine di lavoro aggiornato con successo')
    },
    onError: () => {
      message.error('Errore durante l\'aggiornamento dell\'accettazione')
    },
  })
}

export const useDeleteWorkOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`${API_ENDPOINTS.WORK_ORDERS}/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      message.success('Ordine di lavoro eliminato con successo')
    },
    onError: () => {
      message.error('Errore durante l\'eliminazione dell\'accettazione')
    },
  })
}
